import WebsocketService from "./WebsocketService.jsx";
import SwivuRootMask from "../../bryntum-lib/widget/extendingClasses/SwivuRootMask.jsx";
import { networkStatusStore } from "../../react/hooks/useNetworkState.jsx";
import { SOCKET_MESSAGE_TYPES } from "../constants/OtherKeys.jsx";
import moment from "moment";
import { MessageDialog, StringHelper } from "@bryntum/schedulerpro";
import { applyProjectChangesFromWS } from "../utils/projectUtils.jsx";
import { collectTransactionsForSentryLog } from "../utils/domUtils.jsx";
import { isLocal, SERVER_BASE_URL, WEB_SOCKET_URL_KEY, WEBSOCKET_ACTION_KEY } from "../../env.jsx";

const RECONNECT_MSG = [
  "Please wait while we move the furniture and fill the refrigerator.",
  "Please wait while we feed the hamster.",
  "Loading fuel, priming the engine, and preparing for liftoff.",
  "Please hold while we download instructions from the mother ship."
];
let RECONNECT_MSG_INDEX = 0;
const socketMask = SwivuRootMask.mask({ text: "", style: "z-index: 20000;" });
socketMask.hide();

class SwivuWebSocketService extends WebsocketService {

  constructor({
    project, viewManager, sessionId, board, user, boardManager
  }) {
    socketMask.text = "Enabling Realtime Sync";
    socketMask.show();

    //Todo: make aws websocket url env specific
    const socketUrl = isLocal
      ? `${SERVER_BASE_URL.replace("http", "ws")}/ws/board/${board.id}?token=${user.token}`
      : `wss://${WEB_SOCKET_URL_KEY}?board_id=${board.id}&jwt=${user.token}`;
    super(socketUrl);

    this._webSocketStateListeners = [];
    this.lastSocketResponseTime = Date.now();
    this.project = project;
    this.viewManager = viewManager; // this will be null default but value is assigned later when project loads
    this.boardManager = boardManager;
    this.sessionId = sessionId;
  }

  connect() {
    super.connect();

    this.attachListenerToSocket("message", this.onSocketMessageHandler);
    this.attachListenerToSocket("open", () => {
      this.sendMessage({
        [WEBSOCKET_ACTION_KEY]: SOCKET_MESSAGE_TYPES.REVISION,
        outdated_time: moment(this.lastSocketResponseTime).utc().format()
      });
    });
  }

  onSocketMessageHandler({ data }) {
    const {
      [WEBSOCKET_ACTION_KEY]: action,
      message,
      success,
      revision,
      board_session_id,
      requestId,
      user_id,
      ...stores
    } = StringHelper.safeJsonParse(data);
    const applyProjectsArgs = {
      stores,
      userId: user_id,
      project: this.project,
      viewManager: this.viewManager,
      boardManager: this.boardManager
    };
    if (action === SOCKET_MESSAGE_TYPES.SYNC) {
      // When some changes has been made to the board
      this.lastSocketResponseTime = Date.now();
      // If the board sync message has been sent due to changes made by some other user
      // Socket broadcast changes to the user that initiated those changes
      // therefore, prevent those changes
      if (this.sessionId !== board_session_id) {
        applyProjectChangesFromWS(applyProjectsArgs);
        collectTransactionsForSentryLog(data);
      }
    } else if (action === SOCKET_MESSAGE_TYPES.REVISION && message !== "end") {
      this.lastSocketResponseTime = Date.now();
      // When client request to restore board state.
      applyProjectChangesFromWS(applyProjectsArgs);
      collectTransactionsForSentryLog(data);
    }
  }

  sendMessage(msg) {
    this.send(msg);
  }

  destroy() {
    super.destroy();
    this._notifyStateListeners();
  }

  attachListenerToSocket(eventName, listener) {
    try {
      if (this.ws) {
        this.ws.addEventListener(eventName, listener.bind(this));
      }
    } catch (err) {
      console.log(err);
    }
  }

  doSyncPendingData(isReconnecting) {
    // in case, if destroy is called on websocket then we don't need to sync changes
    if (this.ws && !this.project?.isDestroyed) {
      this.project.autoSync = !isReconnecting;
      if (this.project.autoSync) {
        this.project.sync();
      }
    }
  }

  announceConnectionUpdate(e) {
    super.announceConnectionUpdate(e);

    const { isReconnecting, maxRetriesReached } = e;

    if (maxRetriesReached) {
      const message = `We couldn’t establish a connection after multiple attempts. This is usually caused by a network issue. Please check your internet connection and reload the app.`
      console.error(message);
      socketMask.text = "";
      socketMask.hide();
      MessageDialog.alert({
        title: "Can’t Connect to Server",
        message,
        okButton: "Try Again"
      }).then(() => {
        // I am not sure why, but pressing ESC instead of OK button doesn't reload the page while being reload statement execute correctly
        setTimeout(() => {
          window.location.reload();
        })
      });

      return;
    }

    networkStatusStore.updateNetworkStatus(!isReconnecting);

    this.doSyncPendingData(isReconnecting);
    this._notifyStateListeners();

    if (isReconnecting) {
      socketMask.text = `${RECONNECT_MSG[RECONNECT_MSG_INDEX]} <br/> Your internet connection seems unstable or slow. Please check your network settings or try again later.`;
      socketMask.show();
      RECONNECT_MSG_INDEX = (RECONNECT_MSG_INDEX + 1) % RECONNECT_MSG.length;
    } else {
      socketMask.text = "";
      socketMask.hide();
    }
  }

  // React Specific functions
  _notifyStateListeners() {
    this._webSocketStateListeners.forEach((listener) => {
      listener();
    })
  }

  renderSubscribe(listenerCb) {
    this._webSocketStateListeners = [...this._webSocketStateListeners, listenerCb];
    return () => {
      this._webSocketStateListeners = this._webSocketStateListeners.filter((l) => l !== listenerCb);
    }
  }

  renderSnapShot() {
    return this.isAlive;
  }
}

export default SwivuWebSocketService;
